import { useCallback, useMemo, useState } from 'react';
import { useCreateReportMutation, useGetReportList } from '@/modules/common/queries/reports.ts';
import { GenerateReportRequest, GenerateReportResponse, ReportFormat, ReportStatus, ReportType } from 'api';
import { openInNewTab } from 'ui/utils';

interface GenerateTaxReportArgs {
  registerId: number;
  entityId: number;
  financialYear: number;
  filename: string;
  format?: ReportFormat;
}

export const useGenerateTaxReportDownload = () => {
  const [createdReport, setCreatedReport] = useState<GenerateReportResponse>();

  const handleCreateSuccess = useCallback((data: GenerateReportResponse) => {
    if (data.report.status === ReportStatus.Completed && data.report.file_storage_item_url) {
      openInNewTab(data.report.file_storage_item_url);
      return;
    }

    if (data.report.status === ReportStatus.Generating) {
      setCreatedReport(data);
    }
  }, []);

  const { mutateAsync: createReport, isLoading: isGenerating } = useCreateReportMutation({
    onSuccess: handleCreateSuccess,
  });
  const isDownloading = isGenerating || !!createdReport?.report;

  const reportListPayload = useMemo(
    () => ({
      page: 1,
      page_size: 5,
      type: createdReport?.report?.type,
      entity_id: createdReport?.report?.entity_id,
      register_id: createdReport?.report?.register_id,
      filename: createdReport?.report?.filename,
      format: createdReport?.report?.format,
    }),
    [createdReport?.report],
  );

  const handlePollCompleted = useCallback((reportList?: { data?: Array<{ id: number; status: ReportStatus; file_storage_item_url?: string | null }> }): number | false => {
    if (!createdReport?.report) return false;

    const report = reportList?.data?.find(item => item.id === createdReport.report.id);
    if (!report || report.status === ReportStatus.Generating) return 5000;

    if (report.file_storage_item_url) {
      openInNewTab(report.file_storage_item_url);
    }
    setCreatedReport(undefined);
    return false;
  }, [createdReport?.report]);

  useGetReportList({
    payload: reportListPayload,
    disabled: !createdReport?.report,
    onCompleted: handlePollCompleted,
  });

  const generateTaxReport = useCallback(
    async ({ registerId, entityId, financialYear, filename, format = ReportFormat.Pdf }: GenerateTaxReportArgs) => {
      if (isDownloading) return;

      await createReport({
        type: ReportType.TaxReturn,
        register_id: registerId,
        entity_id: entityId,
        filename,
        format,
        financial_year: financialYear,
        start_date: null,
        end_date: null,
        register_class_ids: null,
      } as unknown as GenerateReportRequest);
    },
    [createReport, isDownloading],
  );

  return {
    generateTaxReport,
    isGenerating,
    isDownloading,
  };
};
